import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import React from 'react';
import { WhatsAppNotificationCard } from '@/components/whatsapp-inbox/WhatsAppNotificationCard';

/**
 * useNotificacaoNota1Gerente — alerta vermelho realtime quando o cliente dá
 * nota RUIM: 1 ou 2 (Douglas 24/09/2026 — antes era só a 1).
 *
 * Recebem o card vermelho:
 *   1. O CONSULTOR que vendeu o pedido — a nota é do atendimento dele
 *   2. Todos os usuários com role='gerente' (Cadija + outros gerentes)
 *   3. Supervisor do consultor (líder da equipe do consultor)
 *
 * Mecânica:
 *   - Subscribe realtime em qualidade_ciclo UPDATE
 *   - Detecta quando nota_atendimento MUDA pra 1 (não-1 → 1)
 *   - Pra cada evento, descobre o supervisor do consultor via:
 *       pedido.consultor_id → usuario.equipe_id → equipe.lider_id
 *   - Mostra card vermelho persistente + som + notificação nativa
 *   - CTA "Atender cliente agora" navega pra conversa
 */
export function useNotificacaoNota1Gerente() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ativosRef = useRef<Map<string, string | number>>(new Map());
  const isGerenteRef = useRef<boolean>(false);

  // Pré-carrega áudio
  useEffect(() => {
    audioRef.current = new Audio('/notification-whatsapp.mp3');
    audioRef.current.volume = 0.7;
  }, []);

  // Detecta se o usuário logado tem role gerente (qualquer um)
  useEffect(() => {
    isGerenteRef.current = profile?.role === 'gerente';
  }, [profile?.role]);

  const tocar = () => {
    try {
      audioRef.current && (audioRef.current.currentTime = 0, audioRef.current.play().catch(() => {}));
    } catch {}
  };

  const showCard = (chave: string, nota: number, nome: string, motivoExtra: string, fotoUrl: string | null, onAbrir: () => void) => {
    const existente = ativosRef.current.get(chave);
    if (existente !== undefined) toast.dismiss(existente);
    tocar();
    const id = toast.custom(
      (t) =>
        React.createElement(WhatsAppNotificationCard, {
          variant: 'nota1',
          titulo: `🚨 ALERTA NOTA ${nota}`,
          nome,
          fotoUrl,
          preview: `Cliente insatisfeito: ${motivoExtra} Atenda no WhatsApp e marque o resultado (Recuperado / Perdido / Anotar). Lembre: nota fica como histórico.`,
          badge: `NOTA ${nota}`,
          ctaLabel: 'Atender cliente agora',
          onAbrir: () => {
            toast.dismiss(t);
            ativosRef.current.delete(chave);
            onAbrir();
          },
          onClose: () => {
            toast.dismiss(t);
            ativosRef.current.delete(chave);
          },
        }),
      { duration: Infinity, position: 'top-right' },
    );
    ativosRef.current.set(chave, id);

    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        if (Notification.permission === 'default') Notification.requestPermission().catch(() => {});
        if (Notification.permission === 'granted' && document.hidden) {
          const native = new Notification(`🚨 NOTA ${nota} — Cliente insatisfeito`, {
            body: `${nome} — atenda agora`,
            icon: '/favicon.ico',
            tag: chave,
            requireInteraction: true,
          });
          native.onclick = () => {
            window.focus();
            toast.dismiss(id);
            ativosRef.current.delete(chave);
            onAbrir();
            native.close();
          };
        }
      } catch {}
    }
  };

  /**
   * Resolve se o usuário logado deve receber notificação pra este ciclo.
   * Retorna true se for gerente OU supervisor do consultor do pedido.
   */
  const deveNotificarUsuario = async (pedido_id: string): Promise<{ deveNotificar: boolean; motivo: string }> => {
    if (!profile?.id) return { deveNotificar: false, motivo: '' };
    const { data: cab } = await supabase.from('pedidos').select('numero_pedido, consultor_id').eq('id', pedido_id).maybeSingle();
    const { data: vendedor } = cab?.consultor_id
      ? await supabase.from('usuarios').select('equipe_id, nome').eq('id', cab.consultor_id).maybeSingle()
      : { data: null };
    const consultorEquipeId = vendedor?.equipe_id;
    const consultorNome = vendedor?.nome || 'consultor';
    const numero = cab?.numero_pedido ? `Pedido ${cab.numero_pedido}, ` : '';
    // 0) O consultor que vendeu recebe: a nota é do atendimento dele.
    if (cab?.consultor_id === profile.id) return { deveNotificar: true, motivo: `${numero}vendido por você.` };
    // 1) Gerente recebe sempre — com o pedido e quem vendeu, como o supervisor.
    if (isGerenteRef.current) return { deveNotificar: true, motivo: `${numero}vendido por ${consultorNome}.` };

    // 2) Verifica se é supervisor (líder de alguma equipe) do consultor do pedido
    if (!consultorEquipeId) return { deveNotificar: false, motivo: '' };

    const { data: eq } = await supabase
      .from('equipes')
      .select('lider_id, nome')
      .eq('id', consultorEquipeId)
      .maybeSingle();
    if ((eq as any)?.lider_id === profile.id) {
      return { deveNotificar: true, motivo: `${numero}atendido por ${consultorNome} (sua equipe).` };
    }
    return { deveNotificar: false, motivo: '' };
  };

  /**
   * A conversa é a MESMA em que o cliente deu a nota (bv_conversa_id) — é ali
   * que ele reclamou e é ali que se atende. O telefone do cadastro só entra
   * quando o ciclo não guardou a conversa.
   */
  const conversaDaNota = async (conversaId: string | null) => {
    if (!conversaId) return null;
    const { data } = await supabase
      .from('whatsapp_conversations')
      .select('id, contact_avatar_url')
      .eq('id', conversaId)
      .maybeSingle();
    return data as { id: string; contact_avatar_url: string | null } | null;
  };

  const acharConversa = async (clienteId: string | null): Promise<string | null> => {
    if (!clienteId) return null;
    const { data: cli } = await supabase
      .from('clientes')
      .select('whatsapp, telefone_principal')
      .eq('id', clienteId)
      .maybeSingle();
    const raw = (cli as any)?.whatsapp || (cli as any)?.telefone_principal || '';
    const dig = String(raw).replace(/\D/g, '');
    if (dig.length < 10) return null;
    const sufixo = dig.slice(-11);
    const { data: convs } = await supabase
      .from('whatsapp_conversations')
      .select('id, wa_id')
      .or(`telefone.like.%${sufixo},wa_id.like.%${sufixo}`);
    return ((convs || [])[0] as any)?.id || null;
  };

  const nomeCliente = async (clienteId: string | null) => {
    if (!clienteId) return 'Cliente';
    const { data } = await supabase
      .from('clientes')
      .select('razao_social, nome_fantasia')
      .eq('id', clienteId)
      .maybeSingle();
    return (data as any)?.nome_fantasia || (data as any)?.razao_social || 'Cliente';
  };

  useEffect(() => {
    if (!profile?.id) return;

    const ch = supabase
      .channel('nota1-gerente-realtime')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'qualidade_ciclo' },
        async (payload) => {
          const nv = payload.new as any;
          const old = payload.old as any;

          // Dispara quando a nota MUDA para 1 ou 2 — nota ruim.
          const nota = Number(nv?.nota_atendimento);
          const virouNotaRuim = (nota === 1 || nota === 2) && old?.nota_atendimento !== nota;
          if (!virouNotaRuim) return;

          // Verifica se este usuário deve receber
          const { deveNotificar, motivo } = await deveNotificarUsuario(nv.pedido_id);
          if (!deveNotificar) return;

          const nome = await nomeCliente(nv?.cliente_id || null);
          const conversa = await conversaDaNota(nv?.bv_conversa_id || null);
          const convId = conversa?.id || await acharConversa(nv?.cliente_id || null);

          qc.invalidateQueries({ queryKey: ['qualidade-ciclos'] });
          qc.invalidateQueries({ queryKey: ['qualidade-stats'] });

          showCard(
            `nota1-${nv.id}`,
            nota,
            nome,
            motivo,
            conversa?.contact_avatar_url || null,
            () => navigate(convId ? `/whatsapp-inbox?conversation=${convId}` : '/whatsapp-inbox'),
          );
        },
      )
      .subscribe();

    return () => { ch.unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);
}
